import { FiBookOpen } from 'react-icons/fi';

const advancedReadingStrategyExercises = [
  {
    title: 'Band 8 Inference Traps',
    type: 'select',
    difficulty: 'Hard',
    bandLevel: 'Band 7.5-8.5',
    instructions:
      'Choose the answer that best reflects the passage without adding your own assumptions.',
    questions: [
      {
        text: 'Passage: "Researchers observed a modest improvement after six weeks, although they noted that the sample size was too small for firm conclusions."\nStatement: "The treatment was definitively proven to be effective."',
        options: ['True', 'False', 'Not Given'],
        answer: 'False',
        explanation:
          'The passage reports only a modest improvement and explicitly says the evidence is not strong enough for firm conclusions.',
      },
      {
        text: 'Passage: "Many urban residents now cycle to work, partly because fuel prices have risen."\nStatement: "Environmental concerns are the main reason people cycle to work."',
        options: ['True', 'False', 'Not Given'],
        answer: 'Not Given',
        explanation:
          'The passage gives one reason, rising fuel prices, but it does not say environmental concerns are the main reason.',
      },
      {
        text: 'Passage: "The museum extended its opening hours during the summer to accommodate demand from international visitors."\nStatement: "The museum changed its schedule because local residents complained."',
        options: ['True', 'False', 'Not Given'],
        answer: 'False',
        explanation:
          'The passage gives a different reason for the change, demand from international visitors.',
      },
    ],
  },
  {
    title: 'Paraphrase Keyword Mapping',
    type: 'fill-in',
    difficulty: 'Hard',
    bandLevel: 'Band 7+',
    instructions:
      'Type a useful keyword or paraphrase clue you would scan for in the passage.',
    questions: [
      {
        text: 'Question stem: "Why did the coastal village become less populated?"\nWrite one strong keyword or paraphrase clue.',
        answer: 'less populated',
        acceptableAnswers: [
          'population decline',
          'decline in population',
          'depopulation',
          'population fell',
        ],
        explanation:
          'High-band readers search for paraphrases such as "decline in population" or "depopulation," not only the exact wording in the question.',
      },
      {
        text: 'Question stem: "What was the main drawback of the original design?"\nWrite one strong keyword or paraphrase clue.',
        answer: 'drawback',
        acceptableAnswers: [
          'disadvantage',
          'weakness',
          'limitation',
          'problem',
        ],
        explanation:
          'IELTS passages often replace "drawback" with synonyms like "limitation" or "weakness."',
      },
    ],
  },
];

export const readingStrategiesData = {
  courseId: 'reading-strategies',
  courseType: 'reading-course',
  title: 'Reading Strategies',
  icon: FiBookOpen,
  overview: {
    impact: 'Master approaches for each question type',
    focus: 'Strategies for IELTS question types',
    uses: 'All reading questions',
    duration: '30 minutes',
  },
  sections: [
    {
      title: 'Question Type Overview',
      content: `<p>IELTS Reading has 11 question types. Here are the most common:</p>`,
      table: {
        headers: ['Question Type', 'Difficulty', 'Strategy'],
        rows: [
          ['True/False/Not Given', 'Hard', 'Match meaning exactly'],
          ['Matching Headings', 'Medium', 'Find main idea per paragraph'],
          ['Gap Fill', 'Medium', 'Scan for keywords'],
          ['Multiple Choice', 'Medium', 'Eliminate wrong answers'],
          ['Matching Information', 'Hard', 'Scan for paraphrase'],
        ],
      },
    },
    {
      title: 'True/False/Not Given Strategy',
      content: `<p>This question type tests whether you can identify exact meaning:</p>`,
      points: [
        'TRUE: The statement matches the passage meaning exactly',
        'FALSE: The statement contradicts the passage',
        'NOT GIVEN: The information is not mentioned or cannot be determined',
      ],
      warning: 'Don\'t use your own knowledge! Only use information from the passage.',
      examples: [
        { label: 'Passage', text: 'The study involved 200 participants from three cities.' },
        { label: 'TRUE', text: '"More than 100 people participated in the study."' },
        { label: 'FALSE', text: '"The study participants came from a single city."' },
        { label: 'NOT GIVEN', text: '"Most participants were female."' },
      ],
    },
    {
      title: 'Matching Headings Strategy',
      content: `<p>Match headings to paragraphs by finding the main idea:</p>`,
      points: [
        'Read all headings first to understand options',
        'Skim each paragraph for its main idea',
        'Focus on the first and last sentences of paragraphs',
        'Eliminate headings as you use them',
        'Watch out for headings that are too specific or too general',
      ],
    },
    {
      title: 'Gap Fill Strategy',
      content: `<p>Complete sentences or summaries with words from the passage:</p>`,
      points: [
        'Read the summary/sentences first to understand context',
        'Predict what type of word is needed (noun, verb, adjective)',
        'Note the word limit (e.g., NO MORE THAN TWO WORDS)',
        'Find the relevant section in the passage',
        'Copy the exact words from the passage—don\'t change them!',
      ],
      warning: 'Stay within the word limit! Hyphenated words count as one word.',
    },
  ],
  exercises: [
    {
      title: 'T/F/NG Practice',
      type: 'select',
      instructions: 'Based on the passage, select the correct answer.',
      questions: [
        {
          text: 'Passage: "The experiment took place in 2019."\nStatement: "The experiment was conducted recently."',
          options: ['True', 'False', 'Not Given'],
          answer: 'Not Given',
          explanation: '"Recently" is subjective; the passage only states 2019.',
        },
        {
          text: 'Passage: "All employees received training."\nStatement: "Some employees were not trained."',
          options: ['True', 'False', 'Not Given'],
          answer: 'False',
          explanation: 'The passage says "all", which contradicts "some...not".',
        },
        {
          text: 'Passage: "The new system reduced errors by 40%."\nStatement: "Errors decreased significantly."',
          options: ['True', 'False', 'Not Given'],
          answer: 'True',
          explanation: '40% reduction can reasonably be called "significant".',
        },
        {
          text: 'Passage: "The study was conducted by researchers at Oxford University."\nStatement: "The research was funded by the British government."',
          options: ['Not Given', 'True', 'False'],
          answer: 'Not Given',
          explanation: 'The passage mentions who did the study, not who funded it.',
        },
        {
          text: 'Passage: "Most participants preferred option A."\nStatement: "Option A was more popular than option B."',
          options: ['True', 'False', 'Not Given'],
          answer: 'True',
          explanation: '"Most preferred A" = A was more popular.',
        },
      ],
    },
    {
      title: 'Identify Question Type',
      type: 'select',
      instructions: 'What type of IELTS Reading question is this?',
      questions: [
        {
          text: 'Match each paragraph (A-E) with the correct heading.',
          options: ['Matching Headings', 'T/F/NG', 'Gap Fill'],
          answer: 'Matching Headings',
          explanation: 'This requires finding the main idea of each paragraph.',
        },
        {
          text: 'Complete the sentences using NO MORE THAN TWO WORDS.',
          options: ['Gap Fill', 'Multiple Choice', 'Matching Information'],
          answer: 'Gap Fill',
          explanation: 'You complete sentences with words from the passage.',
        },
        {
          text: 'Do the following statements agree with the information in the passage?',
          options: ['T/F/NG', 'Multiple Choice', 'Matching Headings'],
          answer: 'T/F/NG',
          explanation: 'This is True/False/Not Given.',
        },
      ],
    },
    {
      title: 'Choose the Best Strategy',
      type: 'select',
      instructions: 'What strategy should you use for each question type?',
      questions: [
        {
          text: 'For Matching Headings questions:',
          options: ['Find the main idea of each paragraph', 'Read every word carefully', 'Focus on specific numbers'],
          answer: 'Find the main idea of each paragraph',
          explanation: 'Headings summarize the main point.',
        },
        {
          text: 'For Gap Fill questions:',
          options: ['Copy exact words from the passage', 'Paraphrase the answer', 'Use synonyms'],
          answer: 'Copy exact words from the passage',
          explanation: 'You must use the exact words—don\'t change them!',
        },
        {
          text: 'For T/F/NG questions:',
          options: ['Match meaning exactly—no assumptions', 'Use your general knowledge', 'Guess if unsure'],
          answer: 'Match meaning exactly—no assumptions',
          explanation: 'Only use information from the passage.',
        },
      ],
    },
    {
      title: 'Practice Finding Keywords',
      type: 'fill-in',
      instructions: 'What keywords would you search for in the passage?',
      questions: [
        {
          text: 'Question: "What percentage of students passed the exam?"',
          answer: 'percentage',
          acceptableAnswers: ['students', 'passed', 'exam'],
          evaluationMode: 'multi-answer',
          explanation: 'Look for numbers (%), "students," "passed," or synonyms.',
        },
        {
          text: 'Question: "When was the company founded?"',
          answer: 'when',
          acceptableAnswers: ['company', 'founded', 'year', 'date'],
          evaluationMode: 'multi-answer',
          explanation: 'Look for dates, years, "established," "started."',
        },
        {
          text: 'Question: "Who developed the new technology?"',
          answer: 'who',
          acceptableAnswers: ['developed', 'technology', 'name'],
          evaluationMode: 'multi-answer',
          explanation: 'Look for names (capitals), "invented," "created."',
        },
      ],
    },
    {
      title: 'Apply Reading Strategies',
      type: 'writing',
      instructions: 'Describe how you would approach each question.',
      questions: [
        {
          text: 'You have 20 minutes and 13 questions on a passage. What is your approach?',
          example: '1. Skim passage (2-3 min) 2. Read questions, underline keywords 3. Scan for answers 4. Answer easier questions first',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['time management', 'question-order strategy', 'keyword scanning'],
          tip: 'Budget time: about 1.5 minutes per question.',
        },
        {
          text: 'A T/F/NG statement says: "The author believes climate change is a serious threat."',
          example: 'Look for opinion markers like "argues," "believes," "claims." The passage must explicitly state the author\'s view—don\'t assume.',
          evaluationMode: 'ai-review',
          aiReviewFocus: ['evidence use', 'T/F/NG reasoning', 'avoiding assumptions'],
          tip: 'Author\'s opinion must be clearly stated, not implied.',
        },
      ],
    },
  ],
  tips: [
    'Read questions before reading passages in detail',
    'Underline keywords in questions',
    'Don\'t spend too long on one question—move on and come back',
    'Practice each question type separately before full tests',
  ],
  dailyPractice: [
    'Practice T/F/NG with short passages',
    'Time yourself on matching heading exercises',
    'Do gap-fill exercises noting word limits',
    'Review wrong answers to understand mistakes',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext: 'IELTS Reading Strategies: Specific approaches for each question type including T/F/NG, matching headings, gap fill, and multiple choice.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...readingStrategiesData,
      title: readingStrategiesData.title + ' (Foundations)',
      overview: {
        ...readingStrategiesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: readingStrategiesData.sections.slice(0, Math.max(2, Math.ceil(readingStrategiesData.sections.length / 2))),
      exercises: readingStrategiesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...readingStrategiesData,
      title: readingStrategiesData.title + ' (Advanced)',
      overview: {
        ...readingStrategiesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: readingStrategiesData.sections,
      exercises: [
        ...readingStrategiesData.exercises.map(ex => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        ...advancedReadingStrategyExercises,
      ],
      tips: [
        ...(readingStrategiesData.tips || []),
        'For Band 8+, distinguish carefully between contradiction and missing information.',
        'Advanced tip: build a paraphrase bank so you can scan for meaning rather than repeated keywords.',
      ],
    };
  }
  return readingStrategiesData;
}
